import React, { useEffect, useMemo, useState } from "react";
import { labels, GENDERS, AVAILABILITIES, SEASONS } from "../i18n/translations";
import { fetchPerfumes } from "../lib/api";
import ProductCard from "../components/ProductCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

// Parse a free-text price like "€ 280" or "280,00 €" into a number, or null.
const parsePrice = (str) => {
  if (!str) return null;
  const match = String(str).replace(/\./g, "").replace(",", ".").match(/\d+(\.\d+)?/);
  if (!match) return null;
  const n = parseFloat(match[0]);
  return Number.isFinite(n) ? n : null;
};

const matchSeason = (productSeasonLabel, selected) => {
  if (selected === "all") return true;
  return String(productSeasonLabel || "")
    .toLowerCase()
    .includes(selected.toLowerCase());
};

export const Catalog = () => {
  const [perfumes, setPerfumes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [brand, setBrand] = useState("all");
  const [gender, setGender] = useState("all");
  const [season, setSeason] = useState("all");
  const [availability, setAvailability] = useState("all");
  const [priceRange, setPriceRange] = useState("all");

  useEffect(() => {
    let mounted = true;
    fetchPerfumes()
      .then((data) => {
        if (!mounted) return;
        setPerfumes(data);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const brands = useMemo(
    () =>
      Array.from(new Set(perfumes.map((p) => p.brand).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b, "it"),
      ),
    [perfumes],
  );

  const priceRanges = [
    { id: "all", label: labels.catalog.all, test: () => true },
    { id: "0-150", label: "fino a € 150", test: (n) => n != null && n <= 150 },
    { id: "150-300", label: "€ 150 — 300", test: (n) => n != null && n > 150 && n <= 300 },
    { id: "300-500", label: "€ 300 — 500", test: (n) => n != null && n > 300 && n <= 500 },
    { id: "500+", label: "oltre € 500", test: (n) => n != null && n > 500 },
  ];

  const filtered = useMemo(() => {
    const priceTest = priceRanges.find((r) => r.id === priceRange)?.test || (() => true);
    return perfumes.filter((p) => {
      if (brand !== "all" && p.brand !== brand) return false;
      if (gender !== "all" && p.gender !== gender) return false;
      if (!matchSeason(p.seasonality, season)) return false;
      if (availability !== "all" && p.availability !== availability) return false;
      if (priceRange !== "all" && !priceTest(parsePrice(p.price))) return false;
      return true;
    });
  }, [perfumes, brand, gender, season, availability, priceRange]);

  const resetFilters = () => {
    setBrand("all");
    setGender("all");
    setSeason("all");
    setAvailability("all");
    setPriceRange("all");
  };

  const anyFilterActive =
    brand !== "all" ||
    gender !== "all" ||
    season !== "all" ||
    availability !== "all" ||
    priceRange !== "all";

  const renderSelect = (id, value, onChange, options, label, testId) => (
    <div className="flex flex-col gap-2 min-w-[160px] flex-1">
      <label htmlFor={id} className="uppercase tracking-[0.22em] text-[10px] text-black/50">
        {label}
      </label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          id={id}
          data-testid={testId}
          className="rounded-none border-0 border-b border-black/30 bg-transparent px-0 h-9 focus:ring-0 focus:border-black uppercase tracking-[0.15em] text-xs"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" data-testid={`${testId}-all`}>
            {labels.catalog.all}
          </SelectItem>
          {options.map((opt) => (
            <SelectItem key={opt.value || opt} value={opt.value || opt} data-testid={`${testId}-${opt.value || opt}`}>
              {opt.label || opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <main data-testid="page-catalog" className="pt-12 md:pt-20">
      <section className="px-6 md:px-12 lg:px-20 pb-12 md:pb-16">
        <div className="uppercase tracking-[0.32em] text-[11px] text-[#C5A059] mb-5">
          {labels.catalog.overline}
        </div>
        <h1
          data-testid="catalog-title"
          className="font-heading text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter leading-none"
        >
          {labels.catalog.title}
        </h1>
        <p className="mt-6 max-w-xl text-base md:text-lg text-black/65 font-light leading-relaxed">
          {labels.catalog.intro}
        </p>
      </section>

      <section className="px-6 md:px-12 lg:px-20 pb-10 md:pb-14">
        <div className="border-y border-black/10 py-6 flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <div className="uppercase tracking-[0.32em] text-[10px] text-black/60">
              {labels.catalog.filters_title}
            </div>
            <div className="uppercase tracking-[0.22em] text-[10px] text-black/40">
              {filtered.length} / {perfumes.length}
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-x-8 gap-y-5">
            {renderSelect(
              "brand-filter",
              brand,
              setBrand,
              brands,
              labels.catalog.filter_brand,
              "filter-brand",
            )}
            {renderSelect(
              "gender-filter",
              gender,
              setGender,
              GENDERS,
              labels.catalog.filter_gender,
              "filter-gender",
            )}
            {renderSelect(
              "season-filter",
              season,
              setSeason,
              SEASONS,
              labels.catalog.filter_season,
              "filter-season",
            )}
            {renderSelect(
              "availability-filter",
              availability,
              setAvailability,
              AVAILABILITIES,
              labels.catalog.filter_availability,
              "filter-availability",
            )}
            {renderSelect(
              "price-filter",
              priceRange,
              setPriceRange,
              priceRanges.filter((r) => r.id !== "all").map((r) => ({ value: r.id, label: r.label })),
              labels.catalog.filter_price,
              "filter-price",
            )}

            {anyFilterActive && (
              <button
                type="button"
                onClick={resetFilters}
                data-testid="filter-reset"
                className="uppercase tracking-[0.22em] text-[10px] text-[#C5A059] hover:text-black transition-colors pb-1.5"
              >
                {labels.catalog.reset}
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
        {loading ? (
          <div className="py-24 text-center uppercase tracking-[0.32em] text-[11px] text-black/40">
            {labels.catalog.loading}
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="catalog-empty"
            className="py-24 text-center font-heading text-2xl md:text-3xl font-light text-black/50"
          >
            {labels.catalog.empty}
          </div>
        ) : (
          <div
            data-testid="catalog-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 md:gap-10"
          >
            {filtered.map((p) => (
              <ProductCard key={p.id} perfume={p} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
};

export default Catalog;
